import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { JotSupportContainer } from '@features/jot-support/integration';
import { useIsAuthenticated, useUserState } from '@features/user';
import { FeatureGates } from '@services/statsig/constants';
import { useFeatureGate } from '@services/statsig/hooks';

import { FRONT_CHAT_CONFIG } from '../config';
import { useFrontChatEvents } from '../hooks/useFrontChatEvents';
import { useFrontChatInit } from '../hooks/useFrontChatInit';
import { useFrontChatVisibility } from '../hooks/useFrontChatVisibility';
import {
  closeChatWindow,
  openChatWindow,
  setInitializationStatus,
  setInitialized,
} from '../store/frontChatSlice';
import {
  selectFrontChatLauncherPosition,
  selectFrontChatLauncherVisible,
  selectFrontChatWindowOpen,
} from '../store/selectors';

import { ChatLauncher } from './ChatLauncher';

/** Both support implementations share the existing screen controls. */
export const FrontChat = () => {
  const jotEnabled = useFeatureGate(FeatureGates.JOT_CUSTOMER_SUPPORT);
  const authenticated = useIsAuthenticated();
  if (!authenticated) return null;
  return jotEnabled ? <JotFrontChat /> : <LegacyFrontChat />;
};

const useCloseSupportOnUnmount = () => {
  const dispatch = useDispatch();
  useEffect(
    () => () => {
      dispatch(closeChatWindow());
    },
    [dispatch],
  );
};

const LegacyFrontChat = () => {
  useCloseSupportOnUnmount();
  const { frontChat, isInitialized } = useFrontChatInit();
  useFrontChatEvents(frontChat, isInitialized);
  useFrontChatVisibility(isInitialized);
  return <ChatLauncher />;
};

const JotFrontChat = () => {
  useCloseSupportOnUnmount();
  const dispatch = useDispatch();
  const authenticated = useIsAuthenticated();
  const user = useUserState({ skip: !authenticated });
  const email = user?.userInfo?.email;
  const ready =
    authenticated &&
    Boolean(user?.user_id) &&
    typeof email === 'string' &&
    Boolean(email) &&
    !FRONT_CHAT_CONFIG.disabledEnvironments.some(
      env => env === String(import.meta.env.VITE_BUILD_ENV),
    );
  const visible = useSelector(selectFrontChatLauncherVisible);
  const open = useSelector(selectFrontChatWindowOpen);
  const position = useSelector(selectFrontChatLauncherPosition);

  useFrontChatVisibility(ready);
  useEffect(() => {
    dispatch(setInitialized(ready));
    dispatch(setInitializationStatus(ready ? 'ready' : 'idle'));
    if (!ready) dispatch(closeChatWindow());
  }, [dispatch, ready]);

  if (!ready || typeof email !== 'string') return null;
  return (
    <JotSupportContainer
      key={user?.user_id}
      replyEmail={email}
      launcherVisible={visible}
      open={open}
      onOpenChange={value => dispatch(value ? openChatWindow() : closeChatWindow())}
      launcherPosition={position}
    />
  );
};
